import React, { useState, useEffect, useRef } from 'react'

import HoverTooltip from '../../../../../../components/HoverTooltip'
import MoneyInput from '../../../../../../components/form/MoneyInput'
import CurrencySelector from '../../../../../../components/currency/CurrencySelector'
import CurrencyExchangeRateInput from '../../../../../../components/currency/CurrencyExchangeRateInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'

import Section from '../../../../../../components/Section'
import {
    Contract,
    ContractPosition,
    ContractPerformance,
    LongTermContractVolumes,
} from '../../../types'

import {
    CurrencyList,
    Okei,
    PlanPositionRow,
} from '../../../../Plans/Positions/Edit/types'

import {
    purchaseTypeInfo,
    purchaseInfo,
    planInfo,
    ContractPositionsInfoError,
} from '../types'

import Checkbox from '../../../../../../components/form/Checkbox'
import ContractPositionTableRow from './ContractTable/ContractPositionTableRow'
import { OrganizationInfo } from '../../../../Org/Home/types'

import ActionMenu, { ActionMenuItem } from './ActionMenu'
import ContractPositionAddFromPlanPopup from './ContractPositionAddFromPlanPopup'
import ContractPositionAddFromContractPopup from './ContractPositionAddFromContractPopup'

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

type Props = {
    theme: string
    contractAction: boolean
    contractGuid: string | undefined
    contract: Contract
    errors?: ContractPositionsInfoError
    currencyList: CurrencyList[]
    contractPositions: ContractPosition[]
    isLoadingContractPosition: boolean
    onUpdatePosition: (
        ordinalNumber: number,
        patch: Partial<ContractPosition>
    ) => void

    onDeletePosition: (ordinalNumber: number) => void
    onDeleteSelected: (ordinalNumbers: number[]) => void
    onCopySelected: (ordinalNumbers: number[]) => void
    onAddEmptyPosition: () => void
    onAddFromPlanRows: (rows: PlanPositionRow[]) => void
    onAddFromContractRows: (rows: ContractPosition[]) => void
    orgData: OrganizationInfo | undefined
}

const ContractPositionInfo: React.FC<Props> = (props) => {
    const [selectedItems, setSelectedItems] = useState<number[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [okeiList, setOkeiList] = useState<Okei[]>([])

    const [
        contractPositionAddFromPlanPopup,
        setContractPositionAddFromPlanPopup,
    ] = useState<boolean>(false)

    const [
        contractPositionMassDeletePopup,
        setContractPositionMassDeletePopup,
    ] = useState<boolean>(false)

    const [contractPositionMassCopyPopup, setContractPositionMassCopyPopup] =
        useState<boolean>(false)

    const [deleteRowContractPositionPopup, setDeleteRowContractPositionPopup] =
        useState<boolean>(false)

    const addBtnRef = useRef<HTMLButtonElement | null>(null)
    const [addMenuOpen, setAddMenuOpen] = useState(false)

    const [addFromPlanPopupOpen, setAddFromPlanPopupOpen] = useState(false)
    const [addFromContractPopupOpen, setAddFromContractPopupOpen] =
        useState(false)

    const addBtnFooterRef = useRef<HTMLButtonElement | null>(null)
    const [addMenuFooterOpen, setAddMenuFooterOpen] = useState(false)

    const addMenuItems: ActionMenuItem[] = [
        {
            key: 'add-new',
            label: 'Добавить новую позицию',
            onClick: () => props.onAddEmptyPosition(),
        },
        {
            key: 'add-from-contract',
            label: 'Добавить позицию с договора',
            onClick: () => setAddFromContractPopupOpen(true),
            disabled: !props.contractPositions.length,
        },
    ]

    const getOkeiList = async (): Promise<Okei[]> => {
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/okei', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на способы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const okei = await getOkeiList()
                if (okei) {
                    setOkeiList(okei)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (ordinal_number: number) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(ordinal_number)) {
                return prevSelectedItems.filter(
                    (itemId) => itemId !== ordinal_number
                )
            } else {
                return [...prevSelectedItems, ordinal_number]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (ordinal_number: number) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(ordinal_number)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, ordinal_number] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            // Выбираем только те элементы, которые соответствуют текущей стадии
            const filteredItems = props.contractPositions
                ? props.contractPositions.map((item) => item.ordinal_number)
                : []

            setSelectedItems(filteredItems)
        } else {
            // Сбрасываем выбор всех элементов
            setSelectedItems([])
        }
    }

    // безопасный парсер десятичных (точка)
    const parseDec = (v: string | number | null | undefined) => {
        if (v === null || v === undefined) return undefined
        if (typeof v === 'number') return v
        const num = Number(String(v).replace(',', '.'))
        return Number.isNaN(num) ? undefined : num
    }

    const handleContractPositionMassDeletePopup = () => {
        setContractPositionMassDeletePopup((prev) => !prev)
    }

    const handleContractPositionMassCopyPopup = () => {
        setContractPositionMassCopyPopup((prev) => !prev)
    }

    const contractPositionsQtySum = Number(
        props.contractPositions
            .reduce((sum, item) => sum + Number(item.qty ?? 0), 0)
            .toFixed(2)
    )

    const contractPositionsSum = Number(
        props.contractPositions
            .reduce(
                (sum, item) =>
                    sum + Number(item.qty ?? 0) * Number(item.unit_price ?? 0),
                0
            )
            .toFixed(2)
    )

    const contractPositionsRubSum = Number(
        props.contractPositions
            .reduce(
                (sum, item) =>
                    sum +
                    Number(item.qty ?? 0) *
                        Number(item.unit_price ?? 0) *
                        (item.currency_digital_code === 643
                            ? 1
                            : Number(item.exchange_rate ?? 0)),
                0
            )
            .toFixed(2)
    )

    return (
        <Section title="Информация о закупаемых товарах, работах, услугах">
            <div className="flex flex-col w-full gap-3 items-start justify-start ">
                <div
                    className={`flex flex-col w-full h-fit items-center justify-center bg-[#e4e6e8] dark:bg-[#242225] rounded ${
                        props.errors?.have_position
                            ? 'border border-[#C15C5C]'
                            : ''
                    }`}
                >
                    <div className="flex flex-row gap-1 p-1 w-full h-[40px] text-[11px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033] items-center justify-center">
                        {!props.contractAction && (
                            <>
                                <div className="flex w-8 h-full items-center justify-center text-center">
                                    <div className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]">
                                        <div
                                            className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                            onClick={handleSelectAllChange}
                                        >
                                            <HoverTooltip tooltipText="Выбрать все">
                                                <input
                                                    type="checkbox"
                                                    className="p-2"
                                                    checked={selectAll}
                                                    onChange={
                                                        handleSelectAllChange
                                                    }
                                                />
                                            </HoverTooltip>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex w-8 h-full items-center justify-center text-center">
                                    <button
                                        ref={addBtnRef}
                                        className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        type="button"
                                        onClick={() =>
                                            setAddMenuOpen((prev) => !prev)
                                        }
                                    >
                                        <HoverTooltip tooltipText="Добавить">
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                id="plus"
                                            >
                                                <path
                                                    fill={
                                                        props.theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                    }
                                                    d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                ></path>
                                            </svg>
                                        </HoverTooltip>
                                    </button>
                                </div>
                                <ActionMenu
                                    open={addMenuOpen}
                                    anchorRef={addBtnRef}
                                    onClose={() => setAddMenuOpen(false)}
                                    items={addMenuItems}
                                    placement="bottom-start"
                                    theme={props.theme}
                                />
                                <div className="flex w-8 h-full items-center justify-center text-center">
                                    <button
                                        className={`w-6 h-6 grid rounded-full place-items-center ${selectedItems.length > 0 ? 'hover:bg-[#FFFFFF] dark:hover:bg-[#656365]' : ''} `}
                                        disabled={!(selectedItems.length > 0)}
                                        onClick={
                                            handleContractPositionMassCopyPopup
                                        }
                                    >
                                        <HoverTooltip tooltipText="Копировать">
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                id="copy"
                                            >
                                                <path
                                                    fill={
                                                        selectedItems.length > 0
                                                            ? props.theme ==
                                                              'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                            : props.theme ==
                                                                'dark'
                                                              ? '#8A8A8A'
                                                              : '#6A6A6A'
                                                    }
                                                    d="M21,8.94a1.31,1.31,0,0,0-.06-.27l0-.09a1.07,1.07,0,0,0-.19-.28h0l-6-6h0a1.07,1.07,0,0,0-.28-.19.32.32,0,0,0-.09,0A.88.88,0,0,0,14.05,2H10A3,3,0,0,0,7,5V6H6A3,3,0,0,0,3,9V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V18h1a3,3,0,0,0,3-3V9S21,9,21,8.94ZM15,5.41,17.59,8H16a1,1,0,0,1-1-1ZM15,19a1,1,0,0,1-1,1H6a1,1,0,0,1-1-1V9A1,1,0,0,1,6,8H7v7a3,3,0,0,0,3,3h5Zm4-4a1,1,0,0,1-1,1H10a1,1,0,0,1-1-1V5a1,1,0,0,1,1-1h3V7a3,3,0,0,0,3,3h3Z"
                                                ></path>
                                            </svg>
                                        </HoverTooltip>
                                    </button>
                                </div>
                                <div className="flex w-8 h-full items-center justify-center text-center">
                                    <button
                                        className={`w-6 h-6 grid rounded-full place-items-center ${selectedItems.length > 0 ? 'hover:bg-[#ed9cae] dark:hover:bg-[#701f1f]' : ''} `}
                                        disabled={!(selectedItems.length > 0)}
                                        onClick={
                                            handleContractPositionMassDeletePopup
                                        }
                                    >
                                        <HoverTooltip tooltipText="Удалить">
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                id="trash"
                                            >
                                                <path
                                                    fill={
                                                        selectedItems.length > 0
                                                            ? props.theme ==
                                                              'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                            : props.theme ==
                                                                'dark'
                                                              ? '#8A8A8A'
                                                              : '#6A6A6A'
                                                    }
                                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                ></path>
                                            </svg>
                                        </HoverTooltip>
                                    </button>
                                </div>
                            </>
                        )}
                        <div className="flex flex-1 w-full h-full items-center justify-center text-center"></div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center border-r border-r-[#b8b8b8] dark:border-r-[#464447]">
                            Позиций <br />
                            {props.contractPositions.length}
                        </div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center border-r border-r-[#b8b8b8] dark:border-r-[#464447]">
                            Количество (объем) <br />
                            {contractPositionsQtySum}
                        </div>
                        <div
                            className={`flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center ${props.contract.currency_digital_code != 643 ? 'border-r border-r-[#b8b8b8] dark:border-r-[#464447]' : ''} `}
                        >
                            Общая сумма <br />
                            {formatNumber(contractPositionsSum)}
                        </div>
                        {props.contract.currency_digital_code != 643 && (
                            <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center">
                                Общая сумма (руб.) <br />
                                {formatNumber(contractPositionsRubSum)}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-row gap-1 w-full h-[40px] p-1 px-1 items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-10 h-full items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Наименование
                        </div>
                        <div className="flex w-[120px] items-center justify-center text-center">
                            Страна <br /> происхождения товара
                        </div>
                        <div className="flex w-[120px] items-center justify-center text-center">
                            Страна регистрации производителя товара
                        </div>
                        <div className="flex w-7 items-center justify-center text-center">
                            <HoverTooltip tooltipText="Невозможно определить количество (объем)">
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 120 160"
                                    id="uncount"
                                >
                                    <path
                                        fill={
                                            props.theme == 'dark'
                                                ? '#8A8A8A'
                                                : '#6A6A6A'
                                        }
                                        d="M100 57c0,0 -1,1 -1,1 0,0 0,1 -1,1 0,0 0,1 0,1 0,0 0,0 0,1 0,0 0,0 0,1 0,0 0,0 0,0 0,1 0,2 1,2 1,2 2,3 4,4 1,0 2,1 3,1 1,0 2,0 3,-1 2,-1 3,-2 4,-4 0,-1 0,-2 0,-2 0,0 0,0 0,0 0,0 0,0 0,0 0,-2 -1,-4 -2,-5 -1,-1 -3,-2 -5,-2 -2,0 -4,1 -5,2l0 0zm-12 -26l-27 -17c-1,0 -1,-1 -2,-1l-1 0c0,0 -1,0 -1,0 0,0 -1,0 -1,0l-1 0c-1,0 -2,0 -2,1l-51 32 -1 0 -1 1 -1 0 0 0 0 1 0 1 0 0c0,0 0,0 0,1 0,0 0,0 0,1l0 60c0,0 0,0 0,0 0,2 1,5 3,6l51 32 0 0 1 0 1 0c1,0 1,0 2,0 1,0 1,0 2,0l1 0 1 0 0 0 51 -31c2,-1 3,-3 3,-6 0,0 0,0 0,0l0 -35c-2,-2 -10,-3 -13,0l0 30 -38 23 0 -44 29 -18c1,-3 -4,-10 -8,-11l-28 17 -38 -23 38 -24 23 14c4,1 10,-8 8,-11zm-38 99l-38 -23 0 -44 38 23 0 43zm54 -130c0,0 0,0 0,0 -7,0 -14,4 -18,10 -1,1 -1,2 -1,4 0,4 3,7 7,7 3,0 5,-1 6,-4 1,-2 3,-3 6,-3 0,0 0,0 0,0 4,0 7,3 7,7 0,4 -3,7 -7,7 -4,0 -7,3 -7,7l0 7c0,4 3,7 7,7 4,0 7,-3 7,-7l0 -1c8,-3 13,-11 13,-19 0,-11 -9,-20 -20,-20l0 0z"
                                    />
                                </svg>
                            </HoverTooltip>
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Количество (объем)
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Единица измерения
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Цена за единицу
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Сумма по позиции
                        </div>
                        {!props.contractAction && (
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                        )}
                        <div className="flex w-8 h-full items-center justify-center text-center" />
                    </div>
                    <div className="flex flex-col w-full h-full gap-1 py-1 overflow-y-auto custom-scrollbar">
                        {props.isLoadingContractPosition ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1 text-[11px] items-start justify-center">
                                {props.contractPositions
                                    ? props.contractPositions.map(
                                          (row, index) => (
                                              <ContractPositionTableRow
                                                  key={row.ordinal_number}
                                                  contractPosition={row}
                                                  currencyList={
                                                      props.currencyList
                                                  }
                                                  selected={selectedItems.includes(
                                                      row.ordinal_number
                                                  )}
                                                  theme={props.theme}
                                                  onClick={(ordinal) => {
                                                      handleCheckboxChange(
                                                          ordinal
                                                      )
                                                  }}
                                                  onRightClick={(ordinal) => {
                                                      handleRightClickCheckboxChange(
                                                          ordinal
                                                      )
                                                  }}
                                                  contract={props.contract}
                                                  okeiList={okeiList}
                                                  contractAction={
                                                      props.contractAction
                                                  }
                                                  onPatch={
                                                      props.onUpdatePosition
                                                  }
                                                  onDelete={
                                                      props.onDeletePosition
                                                  }
                                                  orgData={props.orgData}
                                                  errors={
                                                      props.errors?.positions?.[
                                                          row.ordinal_number
                                                      ]
                                                  }
                                              />
                                          )
                                      )
                                    : ''}
                            </div>
                        )}
                    </div>

                    {/* footer */}
                    <div className="flex flex-row gap-1 w-full h-[40px] text-[11px] p-1 rounded-b items-center justify-start text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex flex-1 flex-row gap-3 px-5 h-full items-center justify-start text-center">
                            {!props.contractAction && (
                                <>
                                    <button
                                        ref={addBtnFooterRef}
                                        className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                        disabled={false}
                                        onClick={() =>
                                            setAddMenuFooterOpen(
                                                (prev) => !prev
                                            )
                                        }
                                    >
                                        <svg
                                            className="w-3 h-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 22 22"
                                            id="plus"
                                        >
                                            <path
                                                fill={`${props.theme == 'dark' ? '#FFFFFF' : '#181818'}`}
                                                d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                            ></path>
                                        </svg>
                                        Добавить
                                    </button>
                                    <ActionMenu
                                        open={addMenuFooterOpen}
                                        anchorRef={addBtnFooterRef}
                                        onClose={() =>
                                            setAddMenuFooterOpen(false)
                                        }
                                        items={addMenuItems}
                                        placement="top-start"
                                        theme={props.theme}
                                    />
                                    <button
                                        className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] hover:bg-[#ffffff] dark:hover:bg-[#656365] bg-[#f6f6f6] dark:bg-[#494849]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'} `}
                                        disabled={!(selectedItems.length > 0)}
                                        onClick={
                                            handleContractPositionMassCopyPopup
                                        }
                                    >
                                        <svg
                                            className="w-3 h-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 22 22"
                                            id="copy"
                                        >
                                            <path
                                                fill={
                                                    selectedItems.length > 0
                                                        ? props.theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                        : props.theme == 'dark'
                                                          ? '#8A8A8A'
                                                          : '#6A6A6A'
                                                }
                                                d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                            ></path>
                                        </svg>
                                        Копировать
                                    </button>
                                    <button
                                        className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] hover:bg-[#ed9cae] dark:hover:bg-[#701f1f] bg-[#f6f6f6] dark:bg-[#494849]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'} `}
                                        disabled={!(selectedItems.length > 0)}
                                        onClick={
                                            handleContractPositionMassDeletePopup
                                        }
                                    >
                                        <svg
                                            className="w-3 h-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 22 22"
                                            id="trash"
                                        >
                                            <path
                                                fill={
                                                    selectedItems.length > 0
                                                        ? props.theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                        : props.theme == 'dark'
                                                          ? '#8A8A8A'
                                                          : '#6A6A6A'
                                                }
                                                d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                            ></path>
                                        </svg>
                                        Удалить
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="flex flex-1 w-full h-full items-center justify-center text-center"></div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center border-r border-r-[#b8b8b8] dark:border-r-[#464447]">
                            Позиций <br />
                            {props.contractPositions.length}
                        </div>
                        <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center border-r border-r-[#b8b8b8] dark:border-r-[#464447]">
                            Количество (объем) <br />
                            {contractPositionsQtySum}
                        </div>
                        <div
                            className={`flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center ${props.contract.currency_digital_code != 643 ? 'border-r border-r-[#b8b8b8] dark:border-r-[#464447]' : ''} `}
                        >
                            Общая сумма <br />
                            {formatNumber(contractPositionsSum)}
                        </div>
                        {props.contract.currency_digital_code != 643 && (
                            <div className="flex flex-col w-48 gap-1 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] items-center justify-center text-center">
                                Общая сумма (руб.) <br />
                                {formatNumber(contractPositionsRubSum)}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* mass copy popup */}
            {contractPositionMassCopyPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Копировать выбранные позиции?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractPositionMassCopyPopup
                                    }
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                    onClick={() => {
                                        props.onCopySelected(selectedItems)
                                        setSelectedItems([])
                                        setSelectAll(false)
                                        handleContractPositionMassCopyPopup()
                                    }}
                                >
                                    Копировать
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* mass delete popup */}
            {contractPositionMassDeletePopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить выбранные позиции?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractPositionMassDeletePopup
                                    }
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={() => {
                                        props.onDeleteSelected(selectedItems)
                                        setSelectedItems([])
                                        setSelectAll(false)
                                        handleContractPositionMassDeletePopup()
                                    }}
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ContractPositionAddFromPlanPopup
                open={addFromPlanPopupOpen}
                theme={props.theme}
                contractPlanGuid={props.contract.plan_guid}
                onClose={() => setAddFromPlanPopupOpen(false)}
                onSelectRows={props.onAddFromPlanRows}
            />

            <ContractPositionAddFromContractPopup
                open={addFromContractPopupOpen}
                theme={props.theme}
                rows={props.contractPositions}
                onClose={() => setAddFromContractPopupOpen(false)}
                onSelectRows={props.onAddFromContractRows}
            />
        </Section>
    )
}

export default ContractPositionInfo
